/**
 * PortalGruppeEditor — the create/edit dialog for one Portal-Berechtigungsgruppe:
 * name + IGB2B category picker (grouped by family) + member (Portal-Nutzer) assignment.
 * Used for both new custom groups and editing templates.
 *
 * The editor collects a desired end-state (name + category set + member set as grant-refs);
 * the caller diffs it against the group's current state and issues the backend's GRANULAR
 * add/remove calls. Members are identified by grant-ref ('primary' | a grant UUID).
 */

import { useState } from 'react';
import { Users } from 'lucide-react';
import { Dialog, Button, TextInput } from '../../administration/components';
import { KategoriePicker } from './KategoriePicker';
import { memberRefOf } from '../api';
import type { Igb2bKategorieDto, PortalGroupDto, PortalMemberOption } from '../types';

/** The editor's desired end-state (member selection carried as grant-refs). */
export interface EditorDraft {
  name: string;
  branchencodes: string[];
  memberRefs: string[];
}

interface Props {
  /** The group being edited, or null for a new group. */
  gruppe: PortalGroupDto | null;
  kategorien: Igb2bKategorieDto[];
  memberPool: PortalMemberOption[];
  saving: boolean;
  error?: string | null;
  onSave: (draft: EditorDraft) => void;
  onClose: () => void;
}

export function PortalGruppeEditor({
  gruppe,
  kategorien,
  memberPool,
  saving,
  error,
  onSave,
  onClose,
}: Props) {
  const [name, setName] = useState(gruppe?.name ?? '');
  const [branchencodes, setBranchencodes] = useState<string[]>(gruppe?.branchencodes ?? []);
  const [memberRefs, setMemberRefs] = useState<string[]>(
    () => gruppe?.members.map((m) => memberRefOf(m.grantId)) ?? [],
  );

  const isNew = gruppe == null;
  const canSave = name.trim().length > 0 && !saving;

  const toggleMember = (ref: string) => {
    setMemberRefs((prev) => (prev.includes(ref) ? prev.filter((x) => x !== ref) : [...prev, ref]));
  };

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={isNew ? 'Neue Berechtigungsgruppe' : `Gruppe bearbeiten: ${gruppe?.name}`}
      description="Legen Sie fest, welche Versicherungskategorien diese Gruppe sieht und welche Portal-Nutzer dazugehören."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => onSave({ name: name.trim(), branchencodes, memberRefs })}
            disabled={!canSave}
          >
            {saving ? 'Wird gespeichert …' : 'Speichern'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div>
          <label htmlFor="gruppe-name" className="mb-1 block text-xs font-medium text-muted-foreground">
            Name
          </label>
          <TextInput
            id="gruppe-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. HR & Personal"
            autoFocus
          />
        </div>

        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            Sichtbare Versicherungskategorien
            <span className="ml-1 tabular-nums">({branchencodes.length})</span>
          </div>
          <KategoriePicker
            kategorien={kategorien}
            selected={branchencodes}
            onChange={setBranchencodes}
          />
        </div>

        <div>
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Users className="size-3.5" /> Mitglieder (Portal-Nutzer)
            <span className="ml-1 tabular-nums">({memberRefs.length})</span>
          </div>
          {memberPool.length === 0 ? (
            <p className="rounded-md border border-dashed border-border bg-card/50 px-3 py-4 text-sm text-muted-foreground">
              Noch keine Portal-Nutzer. Laden Sie zuerst Nutzer über die Einladung ein.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {memberPool.map((u) => {
                const ref = memberRefOf(u.grantId);
                const on = memberRefs.includes(ref);
                return (
                  <label
                    key={ref}
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted/60"
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleMember(ref)}
                      className="size-4 shrink-0 rounded border-border"
                      aria-label={u.label}
                    />
                    <span className="min-w-0 truncate">
                      <span className="text-foreground">{u.label}</span>
                      {u.email && <span className="ml-1 text-xs text-muted-foreground">{u.email}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {error != null && <p className="text-sm text-status-danger">{error}</p>}
      </div>
    </Dialog>
  );
}
